import React, { useState, useEffect } from "react";
import { message, Button, Modal } from "antd";
import {
  fetchShopifyAccountDetails,
  testShopifyConnection,
  createShopifyConnection,
  removeShopifyConnection,
} from "../../../services/SourcesAPIs";

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConnected: () => void;
}

const ShopifyModal: React.FC<ModalProps> = ({ isOpen, onClose, onConnected }) => {
  const api_key = localStorage.getItem("api_key");
  const tenant_id = localStorage.getItem("tenant_id");
  const user_id = localStorage.getItem("user_id");

  const [createLoading, setCreateLoading] = useState(false);
  const [testLoading, setTestLoading] = useState(false);
  const [removeLoading, setRemoveLoading] = useState(false);
  const [accessToken, setAccessToken] = useState("");
  const [storeName, setStoreName] = useState("");
  const [authentication, setAuthentication] = useState("Admin API Access Token");
  const [errorMessage, setErrorMessage] = useState("");
  const [isShopifyConnected, setIsShopifyConnected] = useState(false);
  const [removeConfirmOpen, setRemoveConfirmOpen] = useState(false);

  const dummyResponse = { access_token: "shpat_dummy_token_123" };

  useEffect(() => {
    const fetchExistingAccountDetails = async () => {
      try {
        const result = await fetchShopifyAccountDetails(tenant_id!, api_key!);
        if (result.status && result.shopify_hostname && result.shopify_api_key) {
          setStoreName(result.shopify_hostname);
          setAccessToken(result.shopify_api_key);
          setIsShopifyConnected(true);
        } else {
          setStoreName("");
          setAccessToken("");
          setIsShopifyConnected(false);
        }
      } catch (error: any) {
        setErrorMessage(error.message || "Error fetching Shopify details");
      }
    };

    if (isOpen) {
      fetchExistingAccountDetails();
    }
  }, [isOpen, tenant_id, api_key]);

  const handleContinueWithShopify = () => {
    setAccessToken(dummyResponse.access_token);
    window.open("https://www.shopify.com/store-login", "_blank", "width=500,height=400");
  };

  const createConnection = async () => {
    setErrorMessage("");
    setCreateLoading(true);

    if (!storeName) {
      setErrorMessage("Store name is required.");
      setCreateLoading(false);
      return;
    }

    const data = { accessToken: dummyResponse.access_token, storeURL: storeName, source: "shopify", tenant_id, user_id };

    try {
      const result = await createShopifyConnection(api_key!, data);
      if (result.status) {
        message.success("Connection created successfully.");
        onConnected();
        setIsShopifyConnected(true);
      } else {
        setErrorMessage("Failed to create connection.");
      }
    } catch (error: any) {
      setErrorMessage(error.message || "Error");
    } finally {
      setCreateLoading(false);
    }
  };

  const testConnection = async () => {
    setTestLoading(true);
    setErrorMessage("");

    if (!storeName) {
      setErrorMessage("Please enter the store URL.");
      setTestLoading(false);
      return;
    }

    const data = { accessToken: dummyResponse.access_token, storeURL: storeName, source: "shopify", tenant_id, user_id };

    try {
      await testShopifyConnection(api_key!, data);
      message.success("Connection Tested Successfully");
    } catch (error: any) {
      setErrorMessage(error.message || "Error");
    } finally {
      setTestLoading(false);
    }
  };

  const removeConnectionAPI = async () => {
    setRemoveLoading(true);
    try {
      const result = await removeShopifyConnection(api_key!, tenant_id!);
      if (result.status) {
        message.success("Connection removed successfully.");
        localStorage.setItem("Shopify_connected", "false");
        setStoreName("");
        setAccessToken("");
        setIsShopifyConnected(false);
        setRemoveConfirmOpen(false);
      } else {
        throw new Error(result.message);
      }
    } catch (error: any) {
      setErrorMessage(error.message || "Error");
    } finally {
      setRemoveLoading(false);
    }
  };

  return (
    <>
      <Modal
        title="Connect Shopify"
        open={isOpen}
        onCancel={onClose}
        footer={null}
        width={600}
        centered
      >
       {/* {errorMessage && (
          <div style={{ color: "red", marginBottom: 12 }}>{errorMessage}</div>
        )}*/}

        <div>
          <label>Store</label>
          <input
            type="text"
            className="form-control"
            placeholder="Enter store URL"
            value={storeName}
            onChange={e => setStoreName(e.target.value)}
            readOnly={isShopifyConnected}
          />
          <Button
            type="primary"
            style={{ marginTop: 10 }}
            onClick={handleContinueWithShopify}
          >
            Continue with Shopify
          </Button>
        </div>

        <div style={{ marginTop: 20 }}>
          <label>Authentication</label>
          <select
            className="form-select"
            value={authentication}
            onChange={e => setAuthentication(e.target.value)}
            disabled={isShopifyConnected}
          >
            <option value="Admin API Access Token">Admin API Access Token</option>
            <option value="OAuth 2.0">OAuth 2.0</option>
          </select>

          <div style={{ marginTop: 10 }}>
            <label>Access Token</label>
            <input
              type="password"
              className="form-control"
              value={accessToken}
              readOnly
            />
          </div>
        </div>

        <div style={{ marginTop: 20, display: "flex", gap: 10 }}>
          {isShopifyConnected ? (
            <>
              <Button onClick={() => setRemoveConfirmOpen(true)}>Remove Connection</Button>
              <Button onClick={onClose}>Cancel</Button>
              <Button onClick={() => {
                setStoreName(""); setAccessToken(""); setIsShopifyConnected(false);
              }}>
                Change Account
              </Button>
            </>
          ) : (
            <>
              <Button type="primary" loading={createLoading} onClick={createConnection}>
                {createLoading ? "Creating..." : "Create Connection"}
              </Button>
              <Button onClick={onClose}>Cancel</Button>
              <Button loading={testLoading} onClick={testConnection}>
                {testLoading ? "Testing..." : "Test Connection"}
              </Button>
            </>
          )}
        </div>
      </Modal>

      {/* Remove Confirmation */}
      <Modal
        title="Remove Shopify Account"
        open={removeConfirmOpen}
        onCancel={() => setRemoveConfirmOpen(false)}
        footer={[
          <Button key="cancel" onClick={() => setRemoveConfirmOpen(false)}>Cancel</Button>,
          <Button key="remove" danger loading={removeLoading} onClick={removeConnectionAPI}>
            {removeLoading ? "Removing..." : "Continue"}
          </Button>,
        ]}
        centered
      >
        <p>Removing your account will delete all your existing data. Do you want to continue?</p>
      </Modal>
    </>
  );
};

export default ShopifyModal;
